import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, User, Building2, Target, X, Loader2, FileText, Send, Download } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { cn, getApiErrorMessage } from '@/lib/utils';
import apiClient from '@/lib/api';

interface SearchResult {
  id: string;
  type: 'b2c' | 'b2b' | 'lead' | 'invoice';
  name: string;
  email: string | null;
  phone: string | null;
  company?: string | null;
  vat_number?: string | null;
  tax_code?: string | null;
  // Invoice-specific fields
  invoice_number?: string | null;
  invoice_total?: string | null;
  invoice_date?: string | null;
  invoice_status?: string | null;
}

interface InvoiceLite {
  id: string;
  invoice_number: string | null;
  numero: string | null;
  data_documento: string;
  customer_name: string;
  customer_vat?: string | null;
  totale: string;
  status: string;
}

interface GlobalSearchProps {
  className?: string;
}

export default function GlobalSearch({ className }: GlobalSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { toast } = useToast();
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleOpenSearch = () => {
      setMobileOpen(true);
      setTimeout(() => mobileInputRef.current?.focus(), 100);
    };
    window.addEventListener('open-global-search', handleOpenSearch);
    return () => window.removeEventListener('open-global-search', handleOpenSearch);
  }, []);

  const searchContacts = useCallback(async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const [crmRes, invRes] = await Promise.all([
        apiClient.get<SearchResult[]>(`/crm/global-search?q=${encodeURIComponent(searchQuery)}`),
        apiClient.get<InvoiceLite[]>(`/invoices?search=${encodeURIComponent(searchQuery)}&limit=5`),
      ]);
      const merged: SearchResult[] = [];
      if (crmRes.success && Array.isArray(crmRes.data)) merged.push(...crmRes.data);
      if (invRes.success && Array.isArray(invRes.data)) {
        invRes.data.forEach((inv) => {
          merged.push({
            id: inv.id,
            type: 'invoice',
            name: `${inv.invoice_number || inv.numero || '— bozza —'} · ${inv.customer_name}`,
            email: null,
            phone: null,
            invoice_number: inv.invoice_number || inv.numero,
            invoice_total: inv.totale,
            invoice_date: inv.data_documento,
            invoice_status: inv.status,
            vat_number: inv.customer_vat || null,
          });
        });
      }
      setResults(merged);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length >= 2) {
      setIsLoading(true);
      debounceRef.current = setTimeout(() => {
        searchContacts(query);
      }, 300);
    } else {
      setResults([]);
      setIsLoading(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchContacts]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigateToResult = (result: SearchResult) => {
    setIsOpen(false);
    setQuery('');

    if (result.type === 'invoice') {
      // Task #1466 — invoice ora aperto via tab unificato Documenti Fiscali.
      navigate(`/${tenantSlug}/amministrazione?tab=documenti-fiscali&invoice=${result.id}`);
    } else if (result.type === 'lead') {
      navigate(`/${tenantSlug}/crm?tab=leads&id=${result.id}`);
    } else if (result.type === 'b2b') {
      navigate(`/${tenantSlug}/crm?tab=companies&id=${result.id}`);
    } else {
      navigate(`/${tenantSlug}/crm?tab=contacts&id=${result.id}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          navigateToResult(results[selectedIndex]!);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setQuery('');
        inputRef.current?.blur();
        break;
    }
  };

  const sendInvoice = async (id: string) => {
    setActionInProgress(`send-${id}`);
    try {
      const r = await apiClient.post<{ delayed?: boolean; invoice_number?: string }>(`/invoices/${id}/send`);
      if (r.success) {
        toast({ title: 'Invio richiesto', description: r.data?.delayed ? 'In coda blackout 23:55–00:00' : (r.data?.invoice_number ? `Numero ${r.data.invoice_number}` : 'Trasmessa al provider') });
        setResults((prev) => prev.map((x) => x.id === id ? { ...x, invoice_status: 'sent' } : x));
      } else {
        toast({ title: 'Errore invio', description: r.error || 'Errore', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Errore invio', description: (err as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || 'Errore', variant: 'destructive' });
    } finally { setActionInProgress(null); }
  };

  const downloadInvoicePdf = async (result: SearchResult) => {
    setActionInProgress(`pdf-${result.id}`);
    try {
      const blob = await apiClient.getBlob(`/invoices/${result.id}/download/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${result.invoice_number || result.id}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'PDF non disponibile', description: getApiErrorMessage(err) || (err as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || 'Errore', variant: 'destructive' });
    } finally { setActionInProgress(null); }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'invoice':
        return <FileText className="h-4 w-4 text-indigo-500" />;
      case 'b2b':
        return <Building2 className="h-4 w-4 text-purple-500" />;
      case 'lead':
        return <Target className="h-4 w-4 text-orange-500" />;
      default:
        return <User className="h-4 w-4 text-blue-500" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'invoice':
        return 'Fattura';
      case 'b2b':
        return 'Azienda';
      case 'lead':
        return 'Lead';
      default:
        return 'Contatto';
    }
  };

  const getTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'invoice':
        return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300';
      case 'b2b':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300';
      case 'lead':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300';
      default:
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300';
    }
  };

  const closeMobileSearch = () => {
    setMobileOpen(false);
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  const searchInput = (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
          setSelectedIndex(-1);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Cerca cliente, lead, azienda..."
        aria-label="Cerca cliente, lead, azienda"
        className={cn(
          "w-full pl-9 pr-8 py-2 text-sm rounded-lg",
          "bg-white/80 dark:bg-gray-800/80",
          "border border-gray-200/50 dark:border-gray-700/50",
          "focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500/50 focus:border-blue-500/50",
          "placeholder:text-gray-400 dark:placeholder:text-gray-500",
          "text-gray-900 dark:text-gray-100",
          "shadow-sm hover:shadow transition-shadow duration-200"
        )}
      />
      {query && (
        <button
          onClick={() => {
            setQuery('');
            setResults([]);
            setIsOpen(false);
            inputRef.current?.focus();
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
        >
          <X className="h-3.5 w-3.5 text-gray-400" />
        </button>
      )}
      {isLoading && (
        <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-500 animate-spin" />
      )}
    </div>
  );

  const quickActions = [
    // Task #1466 — quick-actions allineate alla pagina unificata "Documenti Fiscali".
    { id: 'qa-new-document', label: 'Nuovo documento fiscale', hint: 'Apri il picker per scegliere la categoria', icon: <FileText className="h-4 w-4 text-indigo-500" />, run: () => navigate(`/${tenantSlug}/amministrazione?tab=documenti-fiscali&new=1`) },
    { id: 'qa-documenti-fiscali', label: 'Vai a Documenti Fiscali', hint: 'Lista unificata fatture attive, passive, ricevute, note credito/debito', icon: <FileText className="h-4 w-4 text-indigo-500" />, run: () => navigate(`/${tenantSlug}/amministrazione?tab=documenti-fiscali`) },
    { id: 'qa-sezionali', label: 'Vai a Sezionali', hint: 'Configurazione sezionali', icon: <FileText className="h-4 w-4 text-indigo-500" />, run: () => navigate(`/${tenantSlug}/pos/sezionali`) },
    { id: 'qa-corrispettivi', label: 'Vai a Corrispettivi', hint: 'Dashboard corrispettivi', icon: <FileText className="h-4 w-4 text-indigo-500" />, run: () => navigate(`/${tenantSlug}/pos/corrispettivi`) },
  ];
  const filteredQuickActions = (() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return quickActions;
    return quickActions.filter((a) => a.label.toLowerCase().includes(q) || a.hint.toLowerCase().includes(q));
  })();

  const searchResults = isOpen && (
    <div
      ref={dropdownRef}
      className={cn(
        "absolute top-full left-0 right-0 mt-1 z-50",
        "bg-white dark:bg-gray-800",
        "rounded-lg border border-gray-200 dark:border-gray-700",
        "shadow-xl max-h-80 overflow-y-auto"
      )}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span className="text-sm">Ricerca in corso...</span>
        </div>
      ) : results.length > 0 ? (
        <ul className="py-1">
          {results.map((result, index) => (
            <li key={`${result.type}-${result.id}`} className={cn(
              'group relative flex items-stretch',
              'hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150',
              selectedIndex === index && 'bg-blue-50 dark:bg-blue-900/20',
            )}>
              <button
                onClick={() => { navigateToResult(result); if (mobileOpen) closeMobileSearch(); }}
                onMouseEnter={() => setSelectedIndex(index)}
                className="flex-1 px-3 py-2.5 flex items-start gap-3 text-left min-w-0"
              >
                <div className="flex-shrink-0 mt-0.5">
                  {getIcon(result.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white truncate">
                      {result.name}
                    </span>
                    <span className={cn(
                      "text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0",
                      getTypeBadgeClass(result.type)
                    )}>
                      {getTypeLabel(result.type)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    {result.type === 'invoice' && result.invoice_total && (
                      <span className="text-xs font-mono text-gray-700 dark:text-gray-300">
                        € {Number(result.invoice_total).toFixed(2)}
                      </span>
                    )}
                    {result.type === 'invoice' && result.invoice_date && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {result.invoice_date}
                      </span>
                    )}
                    {result.type === 'invoice' && result.invoice_status && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {result.invoice_status}
                      </span>
                    )}
                    {result.email && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {result.email}
                      </span>
                    )}
                    {result.phone && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {result.phone}
                      </span>
                    )}
                    {result.vat_number && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        P.IVA: {result.vat_number}
                      </span>
                    )}
                    {result.tax_code && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        CF: {result.tax_code}
                      </span>
                    )}
                  </div>
                </div>
              </button>
              {result.type === 'invoice' && (
                <div className="flex items-center gap-1 px-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                  {result.invoice_status === 'draft' && (
                    <button
                      type="button"
                      title="Emetti e invia a SDI"
                      aria-label="Emetti e invia a SDI"
                      onClick={(e) => { e.stopPropagation(); sendInvoice(result.id); }}
                      disabled={actionInProgress === `send-${result.id}`}
                      className="p-1.5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 disabled:opacity-50"
                    >
                      {actionInProgress === `send-${result.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    </button>
                  )}
                  {(result.invoice_status === 'sent' || result.invoice_status === 'delivered') && (
                    <button
                      type="button"
                      title="Scarica PDF"
                      aria-label="Scarica PDF"
                      onClick={(e) => { e.stopPropagation(); downloadInvoicePdf(result); }}
                      disabled={actionInProgress === `pdf-${result.id}`}
                      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-50"
                    >
                      {actionInProgress === `pdf-${result.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : query.length >= 2 ? (
        <div className="py-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Nessun risultato trovato
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Prova con un altro termine di ricerca
          </p>
        </div>
      ) : null}
      {filteredQuickActions.length > 0 && (
        <div className="border-t border-gray-100 dark:border-gray-700/60">
          <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
            Azioni rapide
          </div>
          <ul className="pb-1">
            {filteredQuickActions.map((qa) => (
              <li key={qa.id}>
                <button
                  type="button"
                  onClick={() => { qa.run(); setIsOpen(false); setQuery(''); if (mobileOpen) closeMobileSearch(); }}
                  className="w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <span className="flex-shrink-0">{qa.icon}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">{qa.label}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{qa.hint}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className={cn("relative", className)}>
        {searchInput}
        {searchResults}
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={closeMobileSearch} />
          <div className="absolute top-0 left-0 right-0 bg-white dark:bg-gray-900 p-4 shadow-xl">
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={closeMobileSearch}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Ricerca</span>
            </div>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={mobileInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setIsOpen(true);
                    setSelectedIndex(-1);
                  }}
                  onFocus={() => setIsOpen(true)}
                  onKeyDown={handleKeyDown}
                  placeholder="Cerca cliente, lead, azienda..."
                  aria-label="Cerca cliente, lead, azienda"
                  className={cn(
                    "w-full pl-9 pr-8 py-2 text-sm rounded-lg",
                    "bg-white/80 dark:bg-gray-800/80",
                    "border border-gray-200/50 dark:border-gray-700/50",
                    "focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500/50 focus:border-blue-500/50",
                    "placeholder:text-gray-400 dark:placeholder:text-gray-500",
                    "text-gray-900 dark:text-gray-100",
                    "shadow-sm hover:shadow transition-shadow duration-200"
                  )}
                />
                {query && (
                  <button
                    onClick={() => {
                      setQuery('');
                      setResults([]);
                      setIsOpen(false);
                      mobileInputRef.current?.focus();
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    <X className="h-3.5 w-3.5 text-gray-400" />
                  </button>
                )}
                {isLoading && (
                  <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-500 animate-spin" />
                )}
              </div>
              {searchResults}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
